import React, { Suspense } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "./providers/AuthProvider";
import { LoginPage } from "./pages/LoginPage";
import { SignUpPage } from "./pages/SignUpPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import VehicleCard from "./pages/vehicle/VehicleCard";
import Navbar from "./components/Navbar";
import ProtectedRoute from "./utils/ProtectedRoute";
import { RequirePermission } from "./utils/RequirePermission";
import LoadingSpinner from "./components/LoadingSpinner";
import { PAGE_FEATURES_UI } from "./config/featureNav";

// Lazy-loaded routes that are NOT permission-gated (the gated page components
// live in ./config/featureNav.tsx and are emitted from PAGE_FEATURES_UI below).
const ConfigureProviders = React.lazy(() => import("./pages/ConfigureProviders"));
const VehicleDetails = React.lazy(() => import("./pages/VehicleDetails"));
const CustomerSuccessPage = React.lazy(() => import("./pages/customer-success"));

function App(): React.ReactElement {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Public routes that don't require auth
  const publicRoutes = ["/", "/login", "/signup", "/forgot-password", "/reset-password", "/auto-invoice"];


  // Show navbar on all pages except auth pages
  const showNavbar = !publicRoutes.includes(location.pathname);

  // Show loading during initial auth check
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {showNavbar && <Navbar />}

      <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Root route - redirect based on auth status */}
        <Route
          path="/"
          element={
            user ? <Navigate to="/search-vehicle" replace /> : <Navigate to="/login" replace />
          }
        />

        {/* Public auth routes */}
        <Route
          path="/login"
          element={user ? <Navigate to="/search-vehicle" replace /> : <LoginPage />}
        />
        <Route
          path="/signup"
          element={user ? <Navigate to="/search-vehicle" replace /> : <SignUpPage />}
        />
        <Route
          path="/forgot-password"
          element={user ? <Navigate to="/search-vehicle" replace /> : <ForgotPasswordPage />}
        />
        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          {/* /search-vehicle is the base landing page and the default
              redirect target for RequirePermission, so it is intentionally
              left ungated to avoid a redirect loop. */}
          <Route path="/search-vehicle" element={<VehicleCard />} />
          <Route path="/configure-providers" element={<ConfigureProviders />} />
          <Route path="/vehicle/:vehicleInventoryId" element={<VehicleDetails />} />
          <Route path="/customer-success/:stock" element={<CustomerSuccessPage />} />

          {/* Permission-gated page features, emitted from the catalog. These
              guards are UX only — the backend must independently enforce every
              page/entity permission. Do not treat route gating as authorization. */}
          {PAGE_FEATURES_UI.filter((f) => f.routes.length > 0).map((f) => (
            <Route key={f.perm} element={<RequirePermission perm={f.perm} minimum={f.minimum} />}>
              {f.routes.map((r) => (
                <Route key={r.path} path={r.path} element={r.element} />
              ))}
            </Route>
          ))}
        </Route>
      </Routes>
      </Suspense>
    </>
  );
}

export default App;
